import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode
} from "react";
import {
  TranslationKey,
  loadTranslations,
  getTranslations,
  isLanguageLoaded,
  preloadTranslations
} from "../i18n/translations";

export type Language = "en" | "de";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
  availableLanguages: Language[];
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children
}) => {
  const [language, setLanguage] = useState<Language>("en");
  const [isLoading, setIsLoading] = useState(true);
  const [translations, setTranslations] = useState<Record<string, string>>({});

  // Load translations when language changes
  useEffect(() => {
    const loadLanguage = async () => {
      setIsLoading(true);

      // Preload both languages for faster switching
      await preloadTranslations(['en', 'de']);

      // Load the current language
      await loadTranslations(language);

      // Update translations
      setTranslations(getTranslations(language));
      setIsLoading(false);
    };

    loadLanguage();
  }, [language]);

  // Initial language detection
  useEffect(() => {
    const initializeLanguage = async () => {
      // Load saved language preference from localStorage
      const savedLanguage = localStorage.getItem(
        "preferred-language"
      ) as Language;

      let initialLanguage: Language = "en";

      if (savedLanguage) {
        initialLanguage = savedLanguage;
      } else {
        // Detect browser language
        const browserLang = navigator.language.split("-")[0] as Language;
        if (browserLang === "de") {
          initialLanguage = "de";
        }
      }

      setLanguage(initialLanguage);
    };

    initializeLanguage();
  }, []);

  const handleSetLanguage = (lang: Language) => {
    if (lang !== language) {
      setLanguage(lang);
      localStorage.setItem("preferred-language", lang);
      // Update document language attribute for accessibility
      document.documentElement.lang = lang;
    }
  };

  const t = (key: TranslationKey): string => {
    return translations[key] || key;
  };

  const availableLanguages: Language[] = ["en", "de"];

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: handleSetLanguage,
        t,
        availableLanguages,
        isLoading
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
